/**
 * O transporte POR TURNOS — grava → transcreve → manda como mensagem normal
 * → espera a resposta completa → sintetiza → toca (voz v1, 03-04/09/2026,
 * `CLAUDE.md` deste módulo §6.5). É o ÚNICO transporte que existia antes da
 * spec-008-voz-conversacional-tempo-real-chat — extraído aqui ([T020],
 * "refactor puro") para implementar `TransporteDeVoz`
 * (`contracts/transporte.md`) como módulo próprio, em vez de viver como
 * estado global espalhado dentro de `motor-de-voz.js`. Por quê: a spec-008
 * introduz um SEGUNDO transporte (`tempoReal`, [T030], atrás de bandeira)
 * atendendo à MESMA fachada — os dois só cabem sem um `if` em cada método
 * dela se cada implementação morar no PRÓPRIO módulo. `transporte-de-voz.js`
 * ([T021]) é quem escolhe; `motor-de-voz.js` ([T022]) vira fachada fina que
 * delega ao escolhido.
 *
 * **ZERO mudança de comportamento.** Cada função abaixo é o corpo EXATO que
 * vivia em `motor-de-voz.js` — só fechado dentro de
 * `criarTransportePorTurnos()` em vez de solto no escopo do módulo (já era
 * um singleton implícito; segue sendo um, agora atrás de uma factory
 * nomeada). Critério de aceite: a suíte de voz dar o MESMO número de
 * suites/testes antes e depois (`verdicts/linha-de-base-suite.md`, G9) —
 * inclusive `motor-de-voz.unit.js` (não tocado), que segue chamando a
 * fachada pelas MESMAS assinaturas e observando os MESMOS mocks
 * (`./captura-de-voz`, `./vad-de-voz`, ...): continuam valendo porque este
 * arquivo mora no MESMO diretório e importa pelos MESMOS caminhos relativos
 * — `jest.mock()` intercepta por caminho resolvido, não por quem importa.
 *
 * **Duas linguagens, um transporte.** O objeto devolvido expõe: (1) **o de
 * hoje** (`iniciar`/`parar`/`cancelar`/`sairDoModoDeVoz`/`registrarDestino`/
 * `desregistrarDestino`) — o que `motor-de-voz.js` chama, sem mudar de
 * assinatura (é o que faz o [T022] trivial); (2) **o do contrato**
 * (`nome`/`abrir`/`fechar`/`interromper`/`alternarMudo`) — hoje
 * `abrir`/`fechar`/`interromper` são ALIASES finos de `iniciar`/
 * `sairDoModoDeVoz`/`interromperFalaDaIa`. `alternarMudo` é a ÚNICA exceção
 * em que a assinatura mudou: o contrato pede um valor EXPLÍCITO (`mudo:
 * boolean`) — o alternar (ler o atual e inverter) subiu para a fachada, que
 * é quem sabe que "alternar" é o que o botão de mudo pede; este módulo só
 * aplica o valor, com o MESMO guard de antes.
 *
 * Por que os dois vocabulários, e não só o do contrato: a fachada de hoje já
 * tem operações que o mínimo não modela (`parar` manual/automático com
 * desfecho; `cancelar` como descarte silencioso). Forçar tudo pelos cinco
 * verbos AGORA, sem o `tempoReal` para validar a generalização, seria
 * redesenho especulativo — violaria o próprio critério desta task. Fica
 * registrado como o que falta reconciliar quando `tempoReal` ([T030]) e a
 * queda entre os dois ([T034]) existirem de verdade.
 *
 * `abrir(ctx)` aceita hoje só `{modo, avisos}` — não o `ContextoDeSessao`
 * completo (`idConversa`/`obterCredencial`/`delegar`/`marcar`): este
 * transporte não abre canal pago, não delega a resposta por canal próprio
 * (vai pelo `POST /chat` de sempre, via `destino.aoEnviarVoz`) e chama
 * `instrumentacao-de-medicao.js` direto, sem injeção. Divergência
 * DELIBERADA do contrato, documentada em vez de escondida.
 */
import { apiVoz } from '@modules/aawz-intelligence-v2/api/apiVoz'
import {
  GravadorDeTurno,
  suportaEntradaDeVoz,
  MODO_DITAR,
  MODO_VOZ,
} from './captura-de-voz'
import { tocarSomDeInicio, tocarSomDeFim } from './sons-de-voz'
import { motorDeVoz } from './estado-da-voz'
import { criarVadDeAutoEnvio, MIN_CLIPE_MS } from './vad-de-voz'
import { criarEscutaDeBargeIn } from './barge-in-de-voz'
import { criarControleDeFalhaDeStt } from './falha-stt-de-voz'
import {
  registrarReaberturaDeM,
  registrarAvisoDeFalhaDeFala,
  interromperFalaDaIa,
  algoEmVooNaFala,
} from './fala-da-ia'
import { criarObservadorDeSilencio } from './observador-de-silencio'
import {
  marcarFimDaFala,
  marcarInicioDeFala,
  marcarFimDeFalaAtual,
  marcarFalhaDaMedicao,
} from './instrumentacao-de-medicao'
import { TRANSPORTE_POR_TURNOS } from './medicao-de-voz'
import { porTurnosNoComando } from './transporte-no-comando'

// [SHOULD G-2, QA voz] `r.message` de falha de rede/timeout chega da MESMA
// string TÉCNICA crua que `services/connection.js` acrescenta (sufixo
// `(NNN)` do status HTTP) — sem isso o snack mostrava a string crua em vez
// da mensagem de domínio abaixo. O fingerprint é seguro sem tocar o serviço
// compartilhado, porque é este módulo quem já tem a mensagem certa.
const SUFIXO_MENSAGEM_TECNICA = /\(\d+\)\s*$/
const MENSAGEM_PADRAO_FALHA_TRANSCRICAO = 'Não foi possível transcrever o áudio.'

// [spec-019 T003, US-3] Falha de SÍNTESE (erro/prazo/recusa) sem aviso é
// silêncio indistinguível de "ainda pensando" — mensagem FIXA (nunca
// `r.message`), sem código HTTP nem nome de provedor (AC2).
const MENSAGEM_FALHA_DE_SINTESE =
  'Não consegui falar a resposta agora. Ela está escrita na tela.'

/**
 * Fábrica do transporte por turnos — chamada UMA VEZ (por
 * `transporte-de-voz.js`, na carga do módulo). Todo o estado que era
 * módulo-global em `motor-de-voz.js` vira closure daqui.
 *
 * ⚠️ `max-statements` desligado de propósito (precedente já usado no repo:
 * `billing-system/api/apiBillingSystemOverview.js`,
 * `financial-planning/simulacao/financialPlanningSimulation.js`). Este
 * corpo é a extração MECÂNICA do que já era código de MÓDULO (nunca sujeito
 * à regra, porque ela só conta statements DENTRO de função) — agora
 * encapsulado numa factory. As peças são MUTUAMENTE recursivas de propósito
 * (`iniciar` chama `_escutaDeBargeIn`; `parar` chama `transcreverEDistribuir`
 * e `_reabrirSeDescarteAutomatico`, que chama `iniciar` de volta; o VAD de
 * auto-envio criado dentro de `adquirirGravador` chama `parar({manual:
 * false})`): fatiar em sub-factories não reduziria complexidade real, só a
 * relocaria atrás de indireção nova — risco desnecessário num refactor cujo
 * critério é ZERO mudança de comportamento.
 */
// eslint-disable-next-line max-statements
export const criarTransportePorTurnos = () => {
  // Não-reativo de propósito — são objetos/instâncias, não estado de tela.
  let _gravadorDeTurno = null
  let _modoAtual = null
  let _reconhecimentoDeFala = null
  // O composer VISÍVEL no momento é quem recebe o resultado (texto
  // transcrito) — herói e rodapé nunca coexistem, então há sempre no máximo
  // um destino real.
  let _destino = null
  // Os avisos (snacks de falha) do gatilho ORIGINAL — guardados pro re-arme
  // automático poder chamar `iniciar()` de novo sem repasse duplo.
  let _avisosAtuais = null

  // `fala-da-ia.js` chama isto quando a fila esvazia com a sessão ainda
  // ativa (evita import circular).
  registrarReaberturaDeM(() => {
    if (_avisosAtuais) iniciar(MODO_VOZ, _avisosAtuais)
  })

  const resetarEstado = () => {
    motorDeVoz.gravando = false
    motorDeVoz.modoDeVoz = false
    motorDeVoz.estadoDaFala = null
    motorDeVoz.nivelDoMicrofone = 0
    motorDeVoz.microfoneMutado = false
  }

  let _vadDeAutoEnvio = null

  // Estado do VAD é por-turno — sem isto, o "falando" do turno anterior
  // vazaria pro turno novo.
  const _prepararNovoTurnoDeVad = () => {
    if (_vadDeAutoEnvio) _vadDeAutoEnvio.prepararNovoTurno()
  }

  // O `SendMessage` MONTADO registra para onde o texto final vai —
  // `aoPreencherTexto` (Ditar) e `aoEnviarVoz` (Modo de Voz). Chamado no
  // `created()`/`mounted()` de cada instância — herói e rodapé nunca
  // coexistem, então o destino sempre aponta pra quem está na tela.
  const registrarDestino = (destino) => {
    _destino = destino
  }

  const desregistrarDestino = (destino) => {
    if (_destino === destino) _destino = null
  }

  // Preenchimento ao vivo do campo enquanto grava, só no modo Ditar — Web
  // Speech API, gratuita e sem vendor. Ausente fora do Chrome: degrada pra
  // "só preenche ao parar" (o STT do servidor).
  const iniciarReconhecimentoAoVivo = () => {
    const Reconhecimento =
      globalThis.SpeechRecognition || globalThis.webkitSpeechRecognition
    if (!Reconhecimento) return
    const reconhecimento = new Reconhecimento()
    reconhecimento.lang = 'pt-BR'
    reconhecimento.continuous = true
    reconhecimento.interimResults = true
    reconhecimento.onresult = (evento) => {
      let texto = ''
      for (let i = 0; i < evento.results.length; i += 1) {
        texto += evento.results[i][0].transcript
      }
      if (_destino && _destino.aoPreencherTexto) _destino.aoPreencherTexto(texto)
    }
    // Silencioso de propósito: o STT do servidor corrige o rascunho ao
    // parar, então uma falha aqui não precisa de feedback próprio.
    reconhecimento.onerror = () => {}
    try {
      reconhecimento.start()
    } catch (erro) {
      return
    }
    _reconhecimentoDeFala = reconhecimento
  }

  const pararReconhecimentoAoVivo = () => {
    if (!_reconhecimentoDeFala) return
    try {
      _reconhecimentoDeFala.stop()
    } catch (erro) {
      // já parado — nada a fazer.
    }
    _reconhecimentoDeFala = null
  }

  // Pede o microfone e arranca gravação + medidor de nível. `aoNivel`
  // modula a altura do `<ComposerVozOrb>`/`<ComposerDitarEspectro>` — fala
  // normal fica bem abaixo de 1 de RMS, os consumidores amplificam.
  const adquirirGravador = async (avisarSemSuporte, avisarErro) => {
    if (!suportaEntradaDeVoz()) {
      avisarSemSuporte()
      return null
    }
    const gravador = new GravadorDeTurno()
    try {
      await gravador.iniciar({
        aoNivel: (rms) => {
          motorDeVoz.nivelDoMicrofone = rms
          if (_modoAtual === MODO_VOZ) {
            if (!_vadDeAutoEnvio) {
              _vadDeAutoEnvio = criarVadDeAutoEnvio({
                aoLiberar: () => parar({ manual: false }),
              })
            }
            _vadDeAutoEnvio.avaliar(rms)
          }
        },
      })
      return gravador
    } catch (erro) {
      avisarErro((erro && erro.message) || 'Não foi possível acessar o microfone.')
      return null
    }
  }

  const _iniciarEstadoDeGravacao = (gravador, modo) => {
    _gravadorDeTurno = gravador
    _modoAtual = modo
    motorDeVoz.gravando = true
    motorDeVoz.modoDeVoz = modo === MODO_VOZ
    // [feedback do usuário, 04/09] O glow é só do Modo de Voz — o Ditar
    // preenche o campo de texto, sem orb por cima.
    if (motorDeVoz.modoDeVoz) {
      motorDeVoz.estadoDaFala = 'usuario'
      motorDeVoz.sessaoDeVozAtiva = true
    }
    if (modo === MODO_DITAR) iniciarReconhecimentoAoVivo()
    tocarSomDeInicio()
  }

  const iniciar = async (modo, avisos) => {
    if (motorDeVoz.gravando) return
    // [V2] Uma gravação REAL começando solta a escuta de barge-in — nunca
    // dois mics abertos ao mesmo tempo.
    _escutaDeBargeIn.parar()
    // Falar (ou reabrir o mic sozinho) sempre corta a fala da IA em
    // andamento — iniciar UMA gravação nova é, por definição, "quero falar
    // agora".
    interromperFalaDaIa()
    _avisosAtuais = avisos
    _prepararNovoTurnoDeVad()
    const gravador = await adquirirGravador(
      avisos.avisarSemSuporte,
      avisos.avisarErro
    )
    if (!gravador) return
    _iniciarEstadoDeGravacao(gravador, modo)
  }

  const _avisarFalhaDeTranscricao = (r) => {
    if (!_destino || !_destino.avisarErroDeTranscricao) return
    const mensagem = r && r.message
    const mensagemAmigavel =
      mensagem && !SUFIXO_MENSAGEM_TECNICA.test(mensagem)
        ? mensagem
        : MENSAGEM_PADRAO_FALHA_TRANSCRICAO
    // Preserva o resto do envelope (`status`, ...) — `$showSnack` usa
    // `status` pra escolher a cor; só a `message` técnica é substituída.
    _destino.avisarErroDeTranscricao({ ...(r || {}), message: mensagemAmigavel })
  }

  const _distribuirTextoTranscrito = (texto, modo) => {
    if (!texto || !_destino) return
    if (modo === MODO_VOZ) {
      _destino.aoEnviarVoz(texto)
      return
    }
    _destino.aoPreencherTexto(texto)
    if (_destino.focarCampo) _destino.focarCampo()
  }

  /**
   * Chama o STT e interpreta o resultado — extraída de
   * `transcreverEDistribuir` pra manter as duas sob o teto de statements do
   * ESLint (`max-statements`).
   *
   * [G-4 SHOULD, QA voz v3, 08/09] Falha AUTOMÁTICA (VAD, `manual: false`)
   * no Modo de Voz não mostra o aviso genérico aqui —
   * `_controleDeFalhaDeStt.registrarFalha` mostra uma mensagem própria, com
   * a próxima ação. Ditar e a parada MANUAL continuam com o aviso genérico.
   *
   * @returns {Promise<'enviado'|'descartado_sem_fala'|'nada'>}
   */
  const _chamarSttEInterpretar = async (resultado, modo, manual) => {
    motorDeVoz.transcrevendo = true
    try {
      const r = await apiVoz.transcrever({
        audio: resultado.blob,
        contentType: resultado.contentType,
      })
      if (!r || r.status !== 200 || !r.value) {
        if (modo !== MODO_VOZ || manual) _avisarFalhaDeTranscricao(r)
        // [spec-008 T002, N10] falha REAL de STT nunca some da medição — os
        // desfechos `descartado_curto`/`descartado_sem_fala` abaixo não
        // passam por aqui, porque não são falha (contrato do STT). No-op
        // quando nenhuma medição está ativa.
        if (modo === MODO_VOZ) marcarFalhaDaMedicao()
        return 'nada'
      }
      // [V1] `{texto: '', motivo: 'sem_fala'}` — contrato do STT do
      // servidor: não é erro, é a confirmação de que não havia fala. Ignora
      // em silêncio, sem snack.
      if (r.value.motivo === 'sem_fala') return 'descartado_sem_fala'
      const texto = String((r.value && r.value.texto) || '').trim()
      if (!texto) return 'descartado_sem_fala'
      _distribuirTextoTranscrito(texto, modo)
      return 'enviado'
    } finally {
      motorDeVoz.transcrevendo = false
    }
  }

  /**
   * Transcreve o Blob do turno e encaminha o texto pro destino ATUAL.
   *
   * @returns {Promise<'enviado'|'descartado_curto'|'descartado_sem_fala'|'nada'>}
   *   O desfecho — `parar()` usa isto pra decidir se reabre o mic sozinho
   *   (só nos dois `descartado_*`, e só quando NÃO foi parada manual).
   */
  const transcreverEDistribuir = async (gravador, modo, manual) => {
    const resultado = await gravador.parar()
    if (!resultado || !resultado.blob) return 'nada'

    // [V1] Segundo backstop do "nunca mandar silêncio pro STT" — a duração
    // REAL do clipe gravado, não o RMS. Só se aplica ao Modo de Voz: Ditar é
    // sempre manual, o usuário decide quando parar e revisa antes de
    // enviar.
    if (modo === MODO_VOZ && resultado.duracaoSegundos * 1000 < MIN_CLIPE_MS) {
      return 'descartado_curto'
    }

    return _chamarSttEInterpretar(resultado, modo, manual)
  }

  // [G-4 SHOULD, QA voz v3, 08/09] Falha do STT (rede/500) na parada
  // AUTOMÁTICA (VAD) nunca deixa a sessão viva sem escuta — decisão
  // (retry × desistir) vive em `falha-stt-de-voz.js`; aqui só o efeito
  // colateral que só este módulo pode fazer.
  const _avisarFalhaDeVoz = (mensagem) => {
    if (!_destino || !_destino.avisarErroDeTranscricao) return
    _destino.avisarErroDeTranscricao({ status: 500, message: mensagem })
  }

  // `fala-da-ia.js` chama isto no máximo uma vez por turno (ADR-019-04, o
  // contador zera em `iniciarFalaProgressiva`) — o "uma vez" é garantia
  // DELE, não deste callback.
  registrarAvisoDeFalhaDeFala(() => _avisarFalhaDeVoz(MENSAGEM_FALHA_DE_SINTESE))

  const _controleDeFalhaDeStt = criarControleDeFalhaDeStt({
    avisar: _avisarFalhaDeVoz,
    reabrir: () => iniciar(MODO_VOZ, _avisosAtuais),
    desistir: () => {
      motorDeVoz.sessaoDeVozAtiva = false
    },
    podeReabrir: () => Boolean(motorDeVoz.sessaoDeVozAtiva && _avisosAtuais),
  })

  // [V1] Descarte AUTOMÁTICO (não-manual) em sessão contínua reabre o mic
  // sozinho — o VAD nunca deveria ter mandado nada ao STT. Parada MANUAL já
  // encerrou a sessão; nada a reabrir. Extraída pro teto de `max-statements`.
  const _reabrirSeDescarteAutomatico = (desfecho, manual, modo) => {
    if (desfecho !== 'nada') _controleDeFalhaDeStt.resetar()
    const foiDescarte = desfecho === 'descartado_curto' || desfecho === 'descartado_sem_fala'
    if (!manual && foiDescarte && modo === MODO_VOZ && motorDeVoz.sessaoDeVozAtiva && _avisosAtuais) {
      iniciar(MODO_VOZ, _avisosAtuais)
      return
    }
    if (!manual && desfecho === 'nada' && modo === MODO_VOZ) {
      _controleDeFalhaDeStt.registrarFalha()
    }
  }

  /**
   * Para a gravação, transcreve no servidor e distribui o texto pro destino
   * ATUAL. `manual` distingue QUEM pediu: `true` (default, clique no X
   * preto) encerra a SESSÃO inteira; `{manual: false}` (auto-envio do VAD)
   * acaba o turno mas mantém `sessaoDeVozAtiva`, pra reabrir o mic sozinho.
   */
  const parar = async ({ manual = true } = {}) => {
    if (!motorDeVoz.gravando || !_gravadorDeTurno) return
    if (_vadDeAutoEnvio) _vadDeAutoEnvio.limpar()
    pararReconhecimentoAoVivo()
    const gravador = _gravadorDeTurno
    const modo = _modoAtual
    _gravadorDeTurno = null
    // [spec-008 T002] `fim_da_fala` — só no Modo de Voz: Ditar não é
    // "turno" pro contrato de medição. No-op sem medição ativa.
    if (modo === MODO_VOZ) marcarFimDaFala()
    let desfecho = 'nada'
    try {
      desfecho = await transcreverEDistribuir(gravador, modo, manual)
    } finally {
      resetarEstado()
      if (manual) motorDeVoz.sessaoDeVozAtiva = false
      tocarSomDeFim()
    }
    _reabrirSeDescarteAutomatico(desfecho, manual, modo)
  }

  /**
   * Esc CANCELA a gravação em andamento — nem transcreve, nem preenche, nem
   * envia. O Blob nem chega a sair do gravador.
   */
  const cancelar = async () => {
    if (!motorDeVoz.gravando || !_gravadorDeTurno) return
    if (_vadDeAutoEnvio) _vadDeAutoEnvio.limpar()
    pararReconhecimentoAoVivo()
    const modo = _modoAtual
    const gravador = _gravadorDeTurno
    _gravadorDeTurno = null
    // `.parar()` já solta o microfone — o Blob que ele devolve é descartado.
    await gravador.parar()
    resetarEstado()
    // Esc sempre encerra a sessão inteira — sair por cancelamento é de vez.
    motorDeVoz.sessaoDeVozAtiva = false
    tocarSomDeFim()
    // Ditar limpa o rascunho ao vivo do campo — o Web Speech API já
    // preencheu via `@input`, e sobreviveria ao "cancelamento".
    if (modo === MODO_DITAR && _destino && _destino.aoPreencherTexto) {
      _destino.aoPreencherTexto('')
    }
  }

  /**
   * Mudo do Modo de Voz — silencia o microfone SEM parar a gravação (o
   * `MediaRecorder` continua rodando, só grava silêncio no trecho mutado).
   *
   * [T020, contrato `transporte.md`] Recebe o valor EXPLÍCITO — quem decide
   * "alternar" (ler o atual e inverter) é a fachada (`motor-de-voz.js`), que
   * é a dona do verbo sem argumento que `send-message.vue` chama. Este
   * método só aplica o valor, com o MESMO guard de antes.
   */
  const alternarMudo = (mudo) => {
    if (!motorDeVoz.gravando || !_gravadorDeTurno) return
    motorDeVoz.microfoneMutado = Boolean(mudo)
    _gravadorDeTurno.definirMudo(motorDeVoz.microfoneMutado)
  }

  /**
   * Sai do Modo de Voz de vez — interrompe a fala da IA (se houver) e
   * DESCARTA a gravação em andamento (se houver), sem transcrever nem
   * enviar nada. [feedback do usuário, 04/09] Antes disto a única forma de
   * sair enquanto a IA falava era interromper falando por cima (abre uma
   * gravação NOVA); não havia como sair direto, de qualquer estado do ciclo.
   */
  const sairDoModoDeVoz = async () => {
    _escutaDeBargeIn.parar()
    interromperFalaDaIa()
    if (motorDeVoz.gravando) {
      await cancelar()
      return
    }
    motorDeVoz.sessaoDeVozAtiva = false
  }

  /**
   * Barge-in (V2, voz v3) — escuta enquanto a IA fala. A MAQUINARIA (mic,
   * RMS, VAD de barge-in) mora em `barge-in-de-voz.js`; aqui só decidimos
   * QUANDO escutar. `aoDetectarFala` chama `iniciar(MODO_VOZ, ...)`, que já
   * interrompe a fala da IA e começa a gravar o turno novo.
   */
  const _escutaDeBargeIn = criarEscutaDeBargeIn({
    aoDetectarFala: () => iniciar(MODO_VOZ, _avisosAtuais),
    aindaValido: () => motorDeVoz.estadoDaFala === 'ia' && motorDeVoz.sessaoDeVozAtiva,
  })

  /**
   * ★ [spec-008 T030b] A GUARDA DE COMANDO, nos DOIS `$watch` abaixo — o
   * PORQUÊ inteiro (com a mutação que o mediu na [T030]) mora em
   * `transporte-no-comando.js`. Em uma linha: eles observam estado
   * COMPARTILHADO e ficam armados desde a carga do módulo, então uma sessão
   * em tempo real que escrevesse esses campos armaria o barge-in local e o
   * observador de silêncio do v1 — dois juízes do mesmo turno (N4) sem
   * ninguém importar `barge-in-de-voz.js`.
   *
   * Das duas saídas da task, esta é a de DESARMAR (a outra era mover os
   * watchers para dentro da sessão): eles também servem fala que acontece
   * FORA de sessão de voz — a síntese de uma mensagem DIGITADA passa por
   * `estadoDaFala` —, e movê-los mudaria o v1 num caso que a spec não pediu.
   * Com a bandeira desligada `porTurnosNoComando()` é sempre `true`.
   */
  // `estadoDaFala` some sozinho quando o áudio termina (`fala-da-ia.js`) ou
  // quando alguém interrompe (`interromperFalaDaIa`) — observar o campo
  // cobre TODOS os caminhos sem `fala-da-ia.js` precisar conhecer `iniciar()`.
  //
  // [spec-008 T002] Mesma borda alimenta a medição: cada TRANSIÇÃO para
  // `'ia'` é o início de UM segmento de fala reproduzido (ver
  // `instrumentacao-de-medicao.js`). `antigo === 'ia'` no `else` é o que faz
  // a borda de DESCIDA só contar quando a fala estava mesmo tocando.
  motorDeVoz.$watch('estadoDaFala', (novo, antigo) => {
    if (!porTurnosNoComando()) return
    if (novo === 'ia') {
      // ★ [spec-009 T0xx] A escuta só abre COM sessão de voz — a mesma
      // condição que o `aindaValido` do barge-in já exige.
      //
      // ⚠️ Sem esta linha, `iniciar()` chama `pedirMicrofoneComFallback` toda
      // vez que a IA FALA, inclusive fora de sessão nenhuma: o navegador pede
      // permissão de microfone para um disparo que `aindaValido` recusaria em
      // seguida. Custo sem efeito, e agora com consequência — na conversa com
      // o AVATAR (primeiro contato) a IA fala pelo TTS com `sessaoDeVozAtiva`
      // FALSA, e a pessoa veria um pedido de microfone que ninguém provocou.
      //
      // A marca de medição fica FORA da guarda de propósito: a fala aconteceu,
      // e um segmento que some da medição por não ter havido escuta é o
      // buraco que o N10 existe para impedir.
      if (motorDeVoz.sessaoDeVozAtiva) _escutaDeBargeIn.iniciar()
      marcarInicioDeFala()
    } else {
      _escutaDeBargeIn.parar()
      if (antigo === 'ia') marcarFimDeFalaAtual()
    }
  })

  /**
   * [spec-019 T005, US-5] O observador da sessão de voz — a MAQUINARIA
   * (polling, ticks/tentativas, recuperar × desistir) mora em
   * `observador-de-silencio.js`. Aqui só as TRÊS condições legítimas de
   * silêncio (mic armado, IA falando, turno em voo) e o `$watch` que liga o
   * observador à vida da sessão. [AC3] `reproduzirResposta` toca ÁUDIO fora
   * da fila e não aparece em `algoEmVooNaFala()` — cobrir com `estadoDaFala`
   * evita o falso-positivo nesse caso.
   */
  const _estaEmSilencioTerminal = () =>
    motorDeVoz.sessaoDeVozAtiva &&
    !motorDeVoz.gravando &&
    motorDeVoz.estadoDaFala !== 'ia' &&
    !algoEmVooNaFala()

  const _observadorDeSilencio = criarObservadorDeSilencio({
    estaEmSilencioTerminal: _estaEmSilencioTerminal,
    temAvisos: () => Boolean(_avisosAtuais),
    recuperar: () => iniciar(MODO_VOZ, _avisosAtuais),
    avisar: _avisarFalhaDeVoz,
    encerrar: () => {
      motorDeVoz.sessaoDeVozAtiva = false
    },
  })

  // [spec-008 T030b] Guarda de comando — ver o bloco acima do `$watch` de
  // `estadoDaFala`. Sem ela, a sessão em tempo real (que a fachada declara
  // ligando `sessaoDeVozAtiva` para a tela desenhar) ligaria TAMBÉM o
  // observador de silêncio do v1, que tentaria "recuperar" reabrindo o
  // microfone do caminho por turnos — dois microfones na mesma sessão.
  //
  // [AC4] O observador MORRE com a sessão — X/Esc, `parar()`, `cancelar()`
  // e a desistência do G-4 todas passam por `sessaoDeVozAtiva = false`;
  // observar o campo cobre TODOS os caminhos sem cada um lembrar de parar o
  // observador — nada de temporizador órfão (provado em
  // `observador-de-silencio.unit.js` contra `jest.getTimerCount()`).
  motorDeVoz.$watch('sessaoDeVozAtiva', (novo) => {
    if (!porTurnosNoComando()) return
    if (novo) {
      _observadorDeSilencio.iniciar()
    } else {
      _observadorDeSilencio.parar()
    }
  })

  // Verbos do CONTRATO (`contracts/transporte.md`) — ver "Duas linguagens,
  // um transporte" no cabeçalho deste arquivo.
  const abrir = (ctx = {}) => iniciar(ctx.modo, ctx.avisos)
  const fechar = () => sairDoModoDeVoz()
  const interromper = () => interromperFalaDaIa()

  return {
    nome: TRANSPORTE_POR_TURNOS,

    // Contrato (`contracts/transporte.md`).
    abrir,
    fechar,
    interromper,
    alternarMudo,
    registrarDestino,

    // Vocabulário de hoje — o que `motor-de-voz.js` chama, sem mudar de
    // assinatura ([T022]).
    iniciar,
    parar,
    cancelar,
    sairDoModoDeVoz,
    desregistrarDestino,
  }
}
